<template>
  <div class="admin" :class="{ 'admin--nav-open': navOpen }" :style="scaleStyle">
    <!-- ================= 사이드바 ================= -->
    <aside class="admin__side">
      <div class="admin__brand">
        <span class="admin__brand-mark">
          <AppIcon icon="mdi-shield-crown-outline" :size="22" />
        </span>
        <span class="admin__brand-text">
          <strong>함우리몰</strong>
          <em>관리자</em>
        </span>
      </div>

      <div class="admin__me">
        <p class="admin__me-name">{{ admin?.name || '관리자' }}</p>
        <p class="admin__me-meta">
          <span class="admin__role">{{ admin?.roleName || '' }}</span>
          <span>{{ admin?.userId }}</span>
        </p>
      </div>

      <nav class="admin__nav" aria-label="관리자 메뉴">
        <template v-for="group in navGroups" :key="group.title">
          <p class="admin__nav-title">{{ group.title }}</p>
          <router-link
            v-for="item in group.items"
            :key="item.to"
            :to="item.to"
            class="admin__nav-link"
            :class="{ 'is-active': isActive(item.to) }"
            @click="navOpen = false"
          >
            <AppIcon :icon="item.icon" :size="19" />
            <span>{{ item.label }}</span>
            <em v-if="item.badge" class="admin__nav-badge">{{ item.badge }}</em>
          </router-link>
        </template>
      </nav>

      <div class="admin__side-foot">
        <a class="admin__side-link" href="/" target="_blank" rel="noopener">
          <AppIcon icon="mdi-open-in-new" :size="17" />
          사이트 보기
        </a>
        <button type="button" class="admin__side-link" @click="onLogout">
          <AppIcon icon="mdi-logout-variant" :size="17" />
          로그아웃
        </button>
      </div>
    </aside>

    <!-- ================= 본문 ================= -->
    <div class="admin__body">
      <header class="admin__top">
        <button
          type="button"
          class="admin__nav-toggle"
          :aria-expanded="navOpen"
          aria-label="메뉴 열기"
          @click="navOpen = !navOpen"
        >
          <AppIcon :icon="navOpen ? 'mdi-close' : 'mdi-menu'" :size="22" />
        </button>

        <div class="admin__top-title" data-tour="page-title">
          <h1>{{ pageTitle }}</h1>
          <p v-if="pageDesc">{{ pageDesc }}</p>
        </div>

        <div class="admin__top-right">
          <button
            type="button"
            class="admin__visit-btn"
            data-tour="admin-visit-stats"
            @click="visitStatsOpen = true"
          >
            <AppIcon icon="mdi-chart-box-outline" :size="18" />
            방문자 통계
          </button>
          <FeatureSpecPanel />
          <span class="admin__today">{{ today }}</span>
          <router-link v-if="waitingPayment" to="/admin/orders" class="admin__pending is-order">
            <AppIcon icon="mdi-cash-clock" :size="17" />
            입금 확인 {{ waitingPayment }}건
          </router-link>
          <router-link v-if="pendingCount" to="/admin/products?status=REQUESTED" class="admin__pending">
            <AppIcon icon="mdi-clipboard-alert-outline" :size="17" />
            검수요청 {{ pendingCount }}건
          </router-link>
        </div>
      </header>

      <FeatureTourOverlay />

      <main class="admin__main">
        <slot />
      </main>
    </div>

    <div v-if="navOpen" class="admin__scrim" @click="navOpen = false" />

    <div
      v-if="visitStatsOpen"
      class="admin-visit"
      @click.self="visitStatsOpen = false"
      @keydown.esc.prevent="visitStatsOpen = false"
    >
      <div
        class="admin-visit__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-visit-title"
        tabindex="-1"
        ref="visitPanelRef"
      >
        <header class="admin-visit__head">
          <h2 id="admin-visit-title">방문자 통계</h2>
          <button type="button" class="admin-visit__close" aria-label="닫기" @click="visitStatsOpen = false">
            <AppIcon icon="mdi-close" :size="20" />
          </button>
        </header>
        <VisitorStatsWidget plain />
      </div>
    </div>

    <transition name="sui-toast">
      <p v-if="toast" class="sui-toast">
        <AppIcon icon="mdi-check-circle" :size="18" />
        {{ toast }}
      </p>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import FeatureSpecPanel from '@/components/manage/FeatureSpecPanel.vue'
import FeatureTourOverlay from '@/components/manage/FeatureTourOverlay.vue'
import VisitorStatsWidget from '@/components/home/VisitorStatsWidget.vue'
import { useAdminAuth } from '@/composables/useAdminAuth'
import { useManageUiScale } from '@/composables/useManageUiScale'
import { useAdminProducts } from '@/composables/useAdminProducts'
import {
  fetchAdminOrders,
  fetchAdminSettlementRequests,
} from '@/api/mallOrder'

const route = useRoute()
const router = useRouter()
const { admin, logout, isSuperAdmin } = useAdminAuth()
const { scaleStyle } = useManageUiScale()
const { counts, toast, load } = useAdminProducts()

/* 어느 화면으로 들어와도 사이드바 검수 대기 배지는 맞아야 한다 */
onMounted(() => {
  if (!Object.keys(counts.value).length) {
    load()
  }
})

const navOpen = ref(false)

const pendingCount = computed(() => counts.value.REQUESTED ?? 0)
const waitingPayment = ref(0)
const scheduledPayouts = ref(0)

/* 입금·클레임·정산 대기 건수는 사이드바에서 바로 보이게 둔다 */
onMounted(async () => {
  try {
    const [orders, settlements] = await Promise.all([
      fetchAdminOrders({ status: 'PENDING' }).catch(() => null),
      fetchAdminSettlementRequests({ status: 'REQUESTED' }).catch(() => null),
    ])
    waitingPayment.value = (orders?.orders || []).filter((order) => order.payMethod !== 'TOSS').length
    scheduledPayouts.value = settlements?.requestedCount ?? 0
  } catch {
    waitingPayment.value = 0
  }
})

const navGroups = computed(() => [
  {
    title: '현황',
    items: [{ label: '대시보드', to: '/admin', icon: 'mdi-view-dashboard-outline', badge: 0 }],
  },
  {
    title: '주문',
    items: [
      {
        label: '주문 · 입금',
        to: '/admin/orders',
        icon: 'mdi-receipt-text-outline',
        badge: waitingPayment.value,
      },
      {
        label: '정산',
        to: '/admin/settlements',
        icon: 'mdi-cash-multiple',
        badge: scheduledPayouts.value,
      },
    ],
  },
  {
    title: '상품',
    items: [
      {
        label: '상품관리',
        to: '/admin/products',
        icon: 'mdi-package-variant-closed',
        badge: pendingCount.value,
      },
      {
        label: '이달의 특가',
        to: '/admin/deals',
        icon: 'mdi-sale',
        badge: 0,
      },
      {
        label: '공동구매 특별할인',
        to: '/admin/sales',
        icon: 'mdi-calendar-range',
        badge: 0,
      },
      {
        label: '문의 · 후기',
        to: '/admin/cs',
        icon: 'mdi-message-text-outline',
        badge: 0,
      },
    ],
  },
  {
    title: '회원',
    items: [
      { label: '판매업체', to: '/admin/sellers', icon: 'mdi-storefront-outline', badge: 0 },
      {
        label: '고객관리',
        to: '/admin/members',
        icon: 'mdi-office-building-outline',
        badge: 0,
      },
    ],
  },
  {
    title: '설정 · 콘텐츠',
    items: [
      { label: '입금 계좌', to: '/admin/settings', icon: 'mdi-bank-outline', badge: 0 },
      {
        label: '약관 CMS',
        to: '/admin/legal-docs',
        icon: 'mdi-file-document-outline',
        badge: 0,
      },
      {
        label: '화면 멘트',
        to: '/admin/content-snippets',
        icon: 'mdi-text-box-outline',
        badge: 0,
      },
      ...(isSuperAdmin.value
        ? [
            {
              label: '판매업체 화면 보기',
              to: '/admin/seller-view',
              icon: 'mdi-account-switch-outline',
              badge: 0,
            },
          ]
        : []),
    ],
  },
])

function isActive(to: string): boolean {
  if (to === '/admin') {
    return route.path === '/admin'
  }
  return route.path.startsWith(to)
}

const pageTitle = computed(() => (route.meta.adminTitle as string) ?? '관리자')
const pageDesc = computed(() => (route.meta.adminDesc as string) ?? '')

const today = new Intl.DateTimeFormat('ko-KR', {
  month: 'long',
  day: 'numeric',
  weekday: 'short',
}).format(new Date())

const visitStatsOpen = ref(false)
const visitPanelRef = ref<HTMLElement | null>(null)

watch(visitStatsOpen, (open) => {
  if (open) void nextTick(() => visitPanelRef.value?.focus())
})

watch(
  () => route.fullPath,
  () => {
    navOpen.value = false
    visitStatsOpen.value = false
  },
)

async function onLogout() {
  if (!window.confirm('관리자 화면에서 로그아웃할까요?')) {
    return
  }
  await logout()
  router.push({ path: '/login', query: { tab: 'admin' } })
}
</script>

<style lang="scss" src="./AdminLayout.scss"></style>
